import { useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import type { SwipeCallback } from 'react-swipeable';

// Define a Slide type that extends the generic type T to include additional properties.
type TSlide<T> = T & {
  $current?: boolean;
  $previous?: boolean;
  $dir?: 'left' | 'right';
};

interface ISlideState {
  current: number;
  previous: number;
  dir: 'left' | 'right';
}

interface ISwipeOptions {
  preventDefaultTouchmoveEvent: boolean;
  trackMouse: boolean;
  onSwipedLeft?: SwipeCallback;
  onSwipedRight?: SwipeCallback;
  onSwipedUp?: SwipeCallback;
  onSwipedDown?: SwipeCallback;
}

type TDirection = 'left' | 'right' | 'up' | 'down';

const useSlide = <T extends Record<string, any>>(rows: T[], directions: TDirection[]) => {
  const [slideState, setSlideSwipe] = useState<ISlideState>({ current: 0, previous: -1, dir: 'left' });

  const slides = rows.map((slide, index) => {
    const swipedSlides: TSlide<T> = { ...slide, $current: false, $previous: false, $dir: 'left' };
    if (slideState.current === index) {
      swipedSlides.$current = true;
    }
    if (slideState.previous === index) {
      swipedSlides.$previous = true;
    }
    swipedSlides.$dir = slideState.dir;
    return swipedSlides;
  });

  const swipe = (digit: number) => {
    let nextSlide = slideState.current + digit;
    if (nextSlide < 0) {
      nextSlide = slides.length - 1;
    } else if (nextSlide >= slides.length) {
      nextSlide = 0;
    }
    setSlideSwipe({
      current: nextSlide,
      previous: slideState.current,
      dir: digit > 0 ? 'left' : 'right',
    });
  };

  const setThisSlide = (index: number) => {
    if (index === slideState.current) {
      return;
    }
    const dir: 'left' | 'right' = index < slideState.current ? 'right' : 'left';
    setSlideSwipe({
      current: index,
      previous: slideState.current,
      dir,
    });
  };

  const swipeableOptions: ISwipeOptions = {
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  };

  if (directions.includes('left')) {
    swipeableOptions.onSwipedLeft = () => swipe(1);
  }
  if (directions.includes('right')) {
    swipeableOptions.onSwipedRight = () => swipe(-1);
  }
  if (directions.includes('up')) {
    swipeableOptions.onSwipedUp = () => swipe(1);
  }
  if (directions.includes('down')) {
    swipeableOptions.onSwipedDown = () => swipe(-1);
  }

  const swipeable = useSwipeable(swipeableOptions);

  return { slides, setThisSlide, swipeable, swipe, slideState };
};

export default useSlide;
